/**
 * Authentication Provider with SSO Support
 *
 * DESIGN PRINCIPLES:
 * 1. Use ID token for user profile (no Microsoft Graph calls)
 * 2. Use access token for API calls
 * 3. Attempt SSO (silent sign-in) on app load when possible
 * 4. Fall back to interactive login if SSO fails
 * 5. Clear error handling and logging
 *
 * SSO FLOW:
 * 1. On app load, check if SSO is available (domain configured, browser supports it)
 * 2. If SSO available, attempt ssoSilent() to authenticate without user interaction
 * 3. If SSO succeeds, user is automatically logged in
 * 4. If SSO fails, user sees the login page and must click "Sign In"
 */

import React, { createContext, useContext, useState, useEffect, useCallback, useRef } from 'react';
import { useMsal, useIsAuthenticated } from '@azure/msal-react';
import { InteractionStatus } from '@azure/msal-browser';
import { loginRequest, apiRequest, silentSSORequest, getUserFromIdToken } from '../utils/authConfig.js';
import { getRoleFromToken, getUserPermissions } from '../utils/permissions.js';
import { mapAzureRolesToAppRoles } from '../config/roleMapping.js';
import { ssoConfig } from './msalConfig.js';
import appInsightsService from '../services/appInsightsService.js';
import Logger from '../utils/logger.js';

const AuthContext = createContext({
  user: null,
  roles: [],
  permissions: [],
  isAuthenticated: false,
  isLoading: false,
  ssoStatus: null, // SSO status for UI feedback
  login: () => {},
  logout: () => {},
  getAccessToken: () => Promise.resolve(null),
  hasPermission: () => false,
  hasRole: () => false
});

export const useAuth = () => useContext(AuthContext);

export const AuthProvider = ({ children }) => {
  const { instance, accounts, inProgress } = useMsal();
  const isAuthenticated = useIsAuthenticated();
  const [user, setUser] = useState(null);
  const [roles, setRoles] = useState([]);
  const [permissions, setPermissions] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [ssoStatus, setSsoStatus] = useState(null);
  const ssoAttempted = useRef(false);

  /**
   * Get user profile from ID token
   * This is simple and fast - no network calls needed!
   */
  const loadUserProfile = useCallback(() => {
    if (accounts.length === 0) {
      Logger.debug('[AUTH] No accounts found');
      setUser(null);
      setRoles([]);
      setPermissions([]);
      setIsLoading(false);
      return;
    }

    try {
      const account = accounts[0];
      Logger.info('[AUTH] Loading user profile from ID token');

      // Extract user info from ID token claims
      const userData = getUserFromIdToken(account.idTokenClaims);

      if (userData) {
        // Get roles from ID token
        const azureRoles = getRoleFromToken(account.idToken);
        const mappedRoles = mapAzureRolesToAppRoles(azureRoles);
        const userPermissions = getUserPermissions(mappedRoles);

        Logger.info('[AUTH] User profile loaded', {
          userId: userData.id,
          email: userData.email,
          rolesCount: mappedRoles.length,
          permissionsCount: userPermissions.length
        });

        setUser(userData);
        setRoles(mappedRoles);
        setPermissions(userPermissions);

        // Set user context in Application Insights
        appInsightsService.setUser(userData.id, userData.email);
        appInsightsService.trackAuthEvent('user_profile_loaded', {
          hasRoles: mappedRoles.length > 0,
          rolesCount: mappedRoles.length
        });
      } else {
        Logger.warn('[AUTH] Failed to extract user data from ID token');
      }
    } catch (error) {
      Logger.error('[AUTH] Error loading user profile:', error);
      appInsightsService.trackAuthEvent('profile_load_error', {
        error: error.message
      });
    } finally {
      setIsLoading(false);
    }
  }, [accounts]);

  /**
   * Attempt SSO (silent sign-in) when the app loads
   * This will automatically log the user in if they're already signed into Azure AD
   */
  const attemptSSO = useCallback(async () => {
    // Don't attempt SSO if already authenticated or if we've already tried
    if (isAuthenticated || ssoAttempted.current) {
      return;
    }

    // Check if SSO is available
    const ssoAvailable = ssoConfig.isSSOCapable();
    const isDomainJoined = await ssoConfig.isDomainJoined();

    setSsoStatus({
      ssoAvailable,
      isDomainJoined,
      canAttemptSSO: ssoAvailable && isDomainJoined,
      attempting: false,
      succeeded: false,
      error: null
    });

    if (!ssoAvailable) {
      Logger.debug('[AUTH] SSO not available - skipping silent login attempt');
      setIsLoading(false);
      return;
    }

    // Mark that we've attempted SSO
    ssoAttempted.current = true;

    Logger.info('[AUTH] Attempting SSO silent login...');
    setSsoStatus(prev => ({ ...prev, attempting: true }));

    appInsightsService.trackAuthEvent('sso_attempt', {
      ssoAvailable,
      isDomainJoined
    });

    try {
      // First, try to get a token silently if we have accounts in the cache
      const existingAccounts = instance.getAllAccounts();

      if (existingAccounts.length > 0) {
        // We have cached accounts - try to acquire token silently
        Logger.debug('[AUTH] Found cached account, attempting silent token acquisition');

        const response = await instance.acquireTokenSilent({
          ...silentSSORequest,
          account: existingAccounts[0]
        });

        if (response) {
          Logger.info('[AUTH] SSO succeeded using cached account');
          instance.setActiveAccount(existingAccounts[0]);

          // Record successful SSO for future detection
          const email = existingAccounts[0].username || '';
          const domain = email.split('@')[1];
          if (domain) {
            ssoConfig.recordSuccessfulSSO(domain);
          }

          setSsoStatus(prev => ({
            ...prev,
            attempting: false,
            succeeded: true
          }));

          appInsightsService.trackAuthEvent('sso_success', { method: 'cached_account' });
          return; // Authentication state will update via MSAL events
        }
      }

      // No cached accounts or silent acquisition failed - try ssoSilent
      Logger.debug('[AUTH] Attempting ssoSilent with domain hint');

      const ssoResponse = await instance.ssoSilent(silentSSORequest);

      if (ssoResponse && ssoResponse.account) {
        Logger.info('[AUTH] SSO silent login succeeded');
        instance.setActiveAccount(ssoResponse.account);

        // Record successful SSO
        const email = ssoResponse.account.username || '';
        const domain = email.split('@')[1];
        if (domain) {
          ssoConfig.recordSuccessfulSSO(domain);
        }

        setSsoStatus(prev => ({
          ...prev,
          attempting: false,
          succeeded: true
        }));

        appInsightsService.trackAuthEvent('sso_success', { method: 'sso_silent' });
      }
    } catch (error) {
      // SSO failed - this is expected for users not signed into Azure AD
      Logger.debug('[AUTH] SSO silent login failed (expected for non-SSO users):', error.errorCode || error.message);

      setSsoStatus(prev => ({
        ...prev,
        attempting: false,
        succeeded: false,
        error: error.errorCode || error.message
      }));

      appInsightsService.trackAuthEvent('sso_failed', {
        errorCode: error.errorCode,
        errorMessage: error.message
      });

      // Don't throw - SSO failure is expected and user can still log in manually
    } finally {
      setIsLoading(false);
    }
  }, [instance, isAuthenticated]);

  /**
   * Load user profile when authentication state changes
   */
  useEffect(() => {
    if (inProgress === InteractionStatus.None) {
      if (isAuthenticated) {
        loadUserProfile();
      } else {
        // Not authenticated - attempt SSO
        attemptSSO();
      }
    }
  }, [isAuthenticated, inProgress, loadUserProfile, attemptSSO]);

  /**
   * Login function - redirects to Azure AD
   */
  const login = useCallback(async () => {
    try {
      Logger.info('[AUTH] Starting login...');
      appInsightsService.trackAuthEvent('login_attempt');

      await instance.loginRedirect(loginRequest);
    } catch (error) {
      Logger.error('[AUTH] Login error:', error);
      appInsightsService.trackAuthEvent('login_error', {
        error: error.message,
        errorCode: error.errorCode
      });
      throw error;
    }
  }, [instance]);

  /**
   * Logout function
   */
  const logout = useCallback(async () => {
    try {
      Logger.info('[AUTH] Logging out...');
      appInsightsService.trackAuthEvent('logout');

      await instance.logoutRedirect({
        postLogoutRedirectUri: window.location.origin
      });
    } catch (error) {
      Logger.error('[AUTH] Logout error:', error);
      throw error;
    }
  }, [instance]);

  /**
   * Get access token for API calls
   * This is the ONLY token we need for API communication
   */
  const getAccessToken = useCallback(async () => {
    if (accounts.length === 0) {
      Logger.warn('[AUTH] Cannot get access token - no accounts');
      return null;
    }

    try {
      Logger.debug('[AUTH] Acquiring access token...');

      const response = await instance.acquireTokenSilent({
        ...apiRequest,
        account: accounts[0]
      });

      Logger.debug('[AUTH] Access token acquired successfully');
      return response.accessToken;
    } catch (error) {
      Logger.warn('[AUTH] Silent token acquisition failed:', error.errorCode);

      // If silent acquisition fails, try interactive
      if (error.errorCode === 'consent_required' || error.errorCode === 'interaction_required') {
        try {
          Logger.info('[AUTH] Attempting interactive token acquisition...');
          await instance.acquireTokenRedirect({
            ...apiRequest,
            account: accounts[0]
          });
          return null; // Will redirect and come back
        } catch (redirectError) {
          Logger.error('[AUTH] Interactive token acquisition failed:', redirectError);
          throw redirectError;
        }
      }

      throw error;
    }
  }, [instance, accounts]);

  /**
   * Check if user has a specific permission
   */
  const hasPermission = useCallback(
    permission => {
      return permissions.includes(permission);
    },
    [permissions]
  );

  /**
   * Check if user has a specific role
   */
  const hasRole = useCallback(
    role => {
      return roles.includes(role);
    },
    [roles]
  );

  const contextValue = {
    user,
    roles,
    permissions,
    isAuthenticated,
    isLoading,
    ssoStatus,
    login,
    logout,
    getAccessToken,
    hasPermission,
    hasRole
  };

  return <AuthContext.Provider value={contextValue}>{children}</AuthContext.Provider>;
};
